import config from "../../config";
import {Country} from "./country";
import {getCustomerService, ICustomerService} from "../customer/customer-service";
import {Customer} from "../customer/customer";

export const COUNTRY_SERVICE_KEY = 'NY_COUNTRY_SERVICE';
const COUNTRY_KEY = 'NY-COUNTRY';

export function getCountryService(): ICountryService {
  const cs = window[COUNTRY_SERVICE_KEY];
  if (cs) {
    return cs as ICountryService;
  }
  return null
}

declare global {
  // noinspection JSUnusedGlobalSymbols
  interface Window {
    NY_COUNTRY_SERVICE: ICountryService;
  }
}

export interface ICountryService {
  getCountries(): Promise<Country[]>;
  loadCountries(): Promise<Country[]>;
  subscribeForCurrentCountry(tag: string, callback: (country: Country) => void): void;
  setCurrentCountry(country: Country): void;
  getCountryCodeFromLocation(): string;
  getCountryCodeFromUrl(): string;
  getLanguageForCountry(countryCode: string): string;
  routeToCountry(countryCode: string): void;
}

export class CountryService implements ICountryService {
  private countries: Country[];
  private currentCountry: Country;

  private subscribers: any = {};
  private readonly customerService: ICustomerService;

  constructor() {
    this.customerService = getCustomerService();
    // noinspection JSIgnoredPromiseFromCall
    this.initCountry();
  }

  private async initCountry() {
    try {
      this.countries = await this.getCountries();

      const country = this.getCountryFromQueryParameter() || this.getCountryFromLocalStorage();
      if (country) {
        this.setCurrentCountry(country);
        if (country.ny_iso.toLowerCase() !== this.getCountryCodeFromLocation().toLowerCase()) {
          this.routeToCountry(country.ny_iso.toLowerCase());
        }
      }
    } catch (e) {
      console.error('[country-service] error initializing country:', e)
    } finally {
      if (!this.currentCountry) {
        this.currentCountry = null;
        this.notifySubscribers(null);
      }
    }
  }

  private getCountryFromQueryParameter(): Country {
    const countryCode = this.getUrlParam('country');
    return this.countries.find(c => c.iso_3166.toLowerCase() === countryCode?.toLowerCase());
  }

  private getCountryFromLocalStorage(): Country {
    const countryCode = localStorage.getItem(COUNTRY_KEY);
    return this.countries.find(c => c.ny_iso.toLowerCase() === countryCode?.toLowerCase());
  }

  async getCountries(): Promise<Country[]> {
    return this.countries || await this.loadCountries();
  }

  async loadCountries(): Promise<Country[]> {
    try {
      const response = await fetch(config.countryUrl);
      if (response.ok) {
        const data = await response.json();
        const countries = data.map(it => new Country(it));

        return countries.filter(c => !c.disabled);
      }
    } catch (e) {
      console.error("[country-service] loading countries failed: ", e);
    }
    return [];
  }

  getLanguageForCountry(countryCode: string) {
    return this.countries?.find(c => c.iso_3166.toUpperCase() === countryCode.toUpperCase())?.iso_639?? 'DE';
  }

  // noinspection JSUnusedGlobalSymbols
  subscribeForCurrentCountry(tag: string, callback: (country: Country) => void) {
    this.subscribers[tag] = callback;
    if (this.currentCountry !== undefined) {
      callback(this.currentCountry);
    }
  }

  notifySubscribers(country) {
    for (const tag in this.subscribers) {
      if (this.subscribers.hasOwnProperty(tag)) {
        try {
          this.subscribers[tag](country);
        } catch (e) {
          console.log('[country-service] bad subscriber: ', e);
        }
      }
    }
  }

  setCurrentCountry(country: Country) {
    if (this.currentCountry !== country) {
      this.currentCountry = country;
      localStorage.setItem(COUNTRY_KEY, this.currentCountry.ny_iso);
      // noinspection JSIgnoredPromiseFromCall
      this.notifySubscribers(this.currentCountry);
    }
  }

  getCountryCodeFromLocation(): string {
    const fragments = this.getPath().split('/'); // split URL path in segments
    let cc = config.defaultLang;
    if (fragments && fragments.length > 0) {
      cc = fragments[0];
      if (!cc || cc === '' || cc.length > 2) {
        cc = config.defaultLang;
      }
    }
    return cc;
  }

  routeToCountry(countryCode: string) {
    if (!countryCode || countryCode.length !== 2) {
      return
    }
    const baseUrl = this.getBaseUrl();
    const fragments = this.getPath().split('/'); // split URL path in segments
    if (fragments && fragments.length > 0) {
      const cc = fragments[0];
      let path;
      if (!cc || cc === '' || cc.length > 2) {
        if (countryCode.toLowerCase() === config.defaultLang) {
          return
        }
        console.log('no country code in url');
        path = countryCode + '/' + fragments.join('/')
      } else {
        if (countryCode.toLowerCase() === config.defaultLang) {
          //remove country code -> not needed for default-country
          fragments.shift()
        } else {
          //replace country code with the new one
          fragments[0] = countryCode.toLowerCase();
        }
        path = fragments.join('/');
      }

      // remove country query param
      let url = baseUrl + path;
      url = this.removeUrlParam('country', url);

      window.location.href = url;
    }
  }

  // noinspection JSMethodCanBeStatic
  getBaseUrl(): string {
    const url = new URL(location.href);
    if (url.pathname.includes('/victor/preview/')) {
      //preview-mode
      const baseIndex = url.href.indexOf('/victor/preview/') + 16;
      const nextIndex = url.href.substring(baseIndex).indexOf("/") + 1;
      return url.href.substring(0, (baseIndex + nextIndex))
    }

    return url.origin + '/';
  }

  // noinspection JSMethodCanBeStatic
  getPath(): string {
    const url = new URL(location.href);
    if (url.pathname.includes('/victor/preview/')) {
      //preview-mode
      const baseIndex = url.href.indexOf('/victor/preview/') + 16;
      const nextIndex = url.href.substring(baseIndex).indexOf("/") + 1;
      return url.href.substring(baseIndex + nextIndex)
    }

    return url.pathname.substring(1) + url.hash + url.search; // remove leading slash from path
  }

  getCountryCodeFromUrl(): string {
    // keep as alias for compatibility reasons
    return this.getCountryCodeFromLocation();
  }

  // noinspection JSMethodCanBeStatic
  private getUrlParam(name, url?) {
    if (!url) url = window.location.href;
    name = name.replace(/[\[\]]/g, '\\$&');
    const regex = new RegExp('[?&]' + name + '(=([^&#]*)|&|#|$)'),
      results = regex.exec(url);
    if (!results) return null;
    if (!results[2]) return '';
    return decodeURIComponent(results[2].replace(/\+/g, ' '));
  }

  removeUrlParam(parameter: string, url?: string) {
    return (url ? url : location.href)
      .replace(new RegExp('[?&]' + parameter + '=[^&#]*(#.*)?$'), '$1')
      .replace(new RegExp('([?&])' + parameter + '=[^&]*&'), '$1');
  }
}

